import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useDiff, useDiffMutations } from '@/hooks/useDiff';
import { updateEventVerified, updateDiff, updateAsset, deleteDiff } from '@/utils/graphqlClient';
import { setDiffPublished } from '@/utils/externalDiffsAPI';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, Check, Trash2, CheckCheck, X, AlertTriangle, ChevronLeft, ChevronRight, GitMerge, Sparkles, Loader2, MoreVertical, DollarSign, Layers, Zap, Package, BarChart3, HelpCircle, ExternalLink, Eye, EyeOff, Images, Copy, Layout, Square, Plus, ChevronDown } from 'lucide-react';
import { toast } from "sonner";
import { format, parseISO } from 'date-fns';
import { EmbeddedAssetViewer } from '@/components/assets/EmbeddedAssetViewer';
import { SideBySideEditor } from '@/components/assets/SideBySideEditor';
import { EventImageLightbox } from '@/components/assets/EventImageLightbox';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCollectionsQuery, getAssetsQuery } from '@/graphql/queries/getCollections';
import { createAssetMutation } from '@/graphql/mutations/createAsset';

import type { EventChange, DiffDetail } from '@/hooks/useDiff';
import { useAssetById } from '@/hooks/useAssetById';
import { stripCloudinaryTransformations } from '@/utils/cloudinaryUpload';

// Component to toggle between single and side-by-side comparison modes
const ComparisonModeToggle: React.FC<{
  assetId: string;
  onToggle: (assetId: string, mode: 'single' | 'side_by_side') => void;
  refreshKey?: number;
}> = ({ assetId, onToggle, refreshKey }) => {
  const { asset, loading } = useAssetById(assetId);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading...
      </div>
    );
  }

  const currentMode = asset?.comparison_mode || 'side_by_side';

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600 font-medium">View:</span>
      <ToggleGroup
        type="single"
        value={currentMode}
        onValueChange={(value) => {
          if (value) {
            onToggle(assetId, value as 'single' | 'side_by_side');
          }
        }}
        className="bg-white rounded-md border border-gray-300"
      >
        <ToggleGroupItem
          value="side_by_side"
          className="px-3 py-1.5 text-xs data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700"
          aria-label="Side-by-side view"
        >
          <Layout className="h-3.5 w-3.5 mr-1.5" />
          Side-by-Side
        </ToggleGroupItem>
        <ToggleGroupItem
          value="single"
          className="px-3 py-1.5 text-xs data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700"
          aria-label="Single image view"
        >
          <Square className="h-3.5 w-3.5 mr-1.5" />
          Single
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
};

const DiffDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventTypeFromUrl = searchParams.get('eventType');
  const quarterFromUrl = searchParams.get('quarter');
  const verificationFromUrl = searchParams.get('verification');
  const slugFromUrl = searchParams.get('slug');
  const publishedFromUrl = searchParams.get('published');
  const createdFromUrl = searchParams.get('created');

  // Get diffs list from Redux
  const cachedList = useSelector((state: RootState) => state.diffs.listCache);
  const allDiffs = cachedList?.data.diffs || [];
  const totalCount = cachedList?.data.totalCount || 0;

  // Helper function to preserve all search parameters
  const getQueryString = () => {
    const params = new URLSearchParams();
    if (searchParams.get('quarter')) params.set('quarter', searchParams.get('quarter')!);
    if (searchParams.get('eventType')) params.set('eventType', searchParams.get('eventType')!);
    if (searchParams.get('verification')) params.set('verification', searchParams.get('verification')!);
    if (searchParams.get('slug')) params.set('slug', searchParams.get('slug')!);
    if (searchParams.get('published')) params.set('published', searchParams.get('published')!);
    if (searchParams.get('created')) params.set('created', searchParams.get('created')!);
    return params.toString();
  };

  // Convert filter values for API
  const getDiffsFilters = () => {
    const filters: any = {};

    if (slugFromUrl) filters.slug = slugFromUrl;
    if (quarterFromUrl && quarterFromUrl !== 'all') filters.quarter = quarterFromUrl;
    if (eventTypeFromUrl && eventTypeFromUrl !== 'all') filters.event_type = eventTypeFromUrl;
    if (verificationFromUrl && verificationFromUrl !== 'all') filters.verification_status = verificationFromUrl;
    if (publishedFromUrl && publishedFromUrl !== 'all') {
      filters.published = publishedFromUrl === 'published' ? true : false;
    }
    if (createdFromUrl && createdFromUrl !== 'all') {
      const daysAgo = parseInt(createdFromUrl);
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);
      filters.created_after = date.toISOString();
    }

    return filters;
  };

  // Use React Query hook for single diff only
  const { data: diff, isLoading: diffLoading, isError: diffError, refetch: refetchDiff } = useDiff(id!);
  const mutations = useDiffMutations();

  const loading = diffLoading;
  const error = diffError ? 'Failed to load diff details' : null;
  const [verifyingAll, setVerifyingAll] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [showFilter, setShowFilter] = useState<'unverified' | 'all'>('all');
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editedDescription, setEditedDescription] = useState<string>('');
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [targetEventForMerge, setTargetEventForMerge] = useState<EventChange | null>(null);
  const [selectedSourceEventId, setSelectedSourceEventId] = useState<string | null>(null);
  const [eventImageTypes, setEventImageTypes] = useState<Record<string, 'markup' | 'cropped' | 'original'>>({});
  const [editingDiffField, setEditingDiffField] = useState<'hook' | 'description' | null>(null);
  const [editedDiffValue, setEditedDiffValue] = useState<string>('');
  const [aiHelperOpen, setAiHelperOpen] = useState<'hook' | 'description' | null>(null);
  const [aiCustomInstructions, setAiCustomInstructions] = useState<string>('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [eventAiHelperOpen, setEventAiHelperOpen] = useState<string | null>(null); // stores event ID
  const [eventAiCustomInstructions, setEventAiCustomInstructions] = useState<string>('');
  const [eventAiGenerating, setEventAiGenerating] = useState(false);
  const [sideBySideEditorOpen, setSideBySideEditorOpen] = useState(false);
  const [editorUrls, setEditorUrls] = useState<{ url1: string; url2: string } | null>(null);
  const [assetRefreshKey, setAssetRefreshKey] = useState(0);
  const [currentEditingAssetId, setCurrentEditingAssetId] = useState<string | null>(null);
  const [lightboxState, setLightboxState] = useState<{
    isOpen: boolean;
    assetId: string | null;
    currentImage: 'before' | 'after';
  }>({
    isOpen: false,
    assetId: null,
    currentImage: 'after'
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Collection state
  const queryClient = useQueryClient();
  const [isAddingToCollection, setIsAddingToCollection] = useState(false);
  const [lastUsedCollectionId, setLastUsedCollectionId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lastUsedCollection') || null;
    }
    return null;
  });

  // Fetch collections
  const { data: collectionsResponse } = useQuery({
    queryKey: ['collections'],
    queryFn: getCollectionsQuery,
    staleTime: 5 * 60 * 1000,
  });
  const collections = collectionsResponse?.collections || [];

  // Fetch assets to show count
  const { data: assetsResponse, refetch: refetchAssets } = useQuery({
    queryKey: ['assets'],
    queryFn: () => getAssetsQuery(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Keyboard navigation for prev/next diff
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if no modals are open and no input is focused
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPreviousDiff();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextDiff();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [diff, allDiffs]); // Re-attach when diff or list changes

  // Helper function to format version date (yyyymmdd -> readable date)
  const formatVersionDate = (versionStr: string): string => {
    try {
      // Extract date part from version string (assuming format like "20241201" or "20241201_v1")
      const dateMatch = versionStr.match(/(\d{8})/);
      if (!dateMatch) return versionStr;

      const dateStr = dateMatch[1];
      const year = parseInt(dateStr.substring(0, 4));
      const month = parseInt(dateStr.substring(4, 6));
      const day = parseInt(dateStr.substring(6, 8));

      // Create date with explicit values - month is 1-based in YYYYMMDD but 0-based in JS Date
      const date = new Date(year, month - 1, day);
      return format(date, 'MMM d, yyyy');
    } catch {
      return versionStr;
    }
  };

  // Helper function to generate preview URL
  const getPreviewUrl = (slug: string, vAfter: string): string => {
    // Extract date from version string (yyyymmdd format)
    const dateMatch = vAfter.match(/(\d{8})/);
    const version = dateMatch ? dateMatch[1] : vAfter;
    return `https://pricingsaas.com/news/${slug}/${version}?v2=true`;
  };

  // Handler to open preview in new tab
  const handlePreview = () => {
    if (diff) {
      const previewUrl = getPreviewUrl(diff.slug, diff.v_after);
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Helper function to get unique change types
  const getUniqueChangeTypes = () => {
    if (!diff?.changes) return [];
    const types = new Set(diff.changes.map(change => change.event_type || 'other'));
    return Array.from(types).sort();
  };

  // Helper function to get change type config (icon, color, label)
  const getChangeTypeConfig = (type: string) => {
    const configs: Record<string, { icon: any; color: string; bgColor: string; label: string }> = {
      price: {
        icon: DollarSign,
        color: 'text-green-700',
        bgColor: 'bg-green-100',
        label: 'Price'
      },
      plan: {
        icon: Layers,
        color: 'text-blue-700',
        bgColor: 'bg-blue-100',
        label: 'Plans'
      },
      feature: {
        icon: Zap,
        color: 'text-purple-700',
        bgColor: 'bg-purple-100',
        label: 'Feature'
      },
      discount: {
        icon: Package,
        color: 'text-orange-700',
        bgColor: 'bg-orange-100',
        label: 'Discount'
      },
      metric: {
        icon: BarChart3,
        color: 'text-indigo-700',
        bgColor: 'bg-indigo-100',
        label: 'Metric'
      },
      other: {
        icon: HelpCircle,
        color: 'text-gray-700',
        bgColor: 'bg-gray-100',
        label: 'Other'
      }
    };
    return configs[type] || configs.other;
  };


  // Get filtered diffs from Redux (already filtered from Diffs page)
  const getFilteredDiffs = () => {
    return allDiffs || [];
  };

  // Navigation functions for going to previous/next diff
  const goToPreviousDiff = () => {
    if (!diff) return;

    const filteredDiffs = getFilteredDiffs();
    const currentIndex = filteredDiffs.findIndex(d => d.id === diff.id);

    if (currentIndex > 0) {
      const prevDiff = filteredDiffs[currentIndex - 1];
      // Preserve all filters when navigating
      const queryString = getQueryString();
      navigate(`/diffs/${prevDiff.id}${queryString ? `?${queryString}` : ''}`);
    }
  };

  const goToNextDiff = () => {
    if (!diff) return;

    const filteredDiffs = getFilteredDiffs();
    const currentIndex = filteredDiffs.findIndex(d => d.id === diff.id);

    if (currentIndex < filteredDiffs.length - 1) {
      const nextDiff = filteredDiffs[currentIndex + 1];
      // Preserve all filters when navigating
      const queryString = getQueryString();
      navigate(`/diffs/${nextDiff.id}${queryString ? `?${queryString}` : ''}`);
    }
  };

  const getCurrentDiffPosition = () => {
    if (!diff) return { current: 0, total: totalCount };

    const filteredDiffs = getFilteredDiffs();
    const currentIndex = filteredDiffs.findIndex(d => d.id === diff.id);

    return {
      current: currentIndex !== -1 ? currentIndex + 1 : 0,
      total: totalCount // Always use totalCount from Redux, not array length
    };
  };

  // Utility function to get event status
  const getEventStatus = (change: EventChange) => {
    if (change.verified) return 'verified';
    if (change.partially_correct) return 'partially_correct';
    if (change.invalid) return 'invalid';
    return 'unverified';
  };

  // Utility function to get status display
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'verified':
        return { label: 'Verified', variant: 'default' as const };
      case 'partially_correct':
        return { label: 'Partially Correct', variant: 'secondary' as const };
      case 'invalid':
        return { label: 'Invalid', variant: 'destructive' as const };
      default:
        return { label: 'Unverified', variant: 'outline' as const };
    }
  };

  const checkForNextDiff = async (currentDiffId: string) => {
    try {
      // Use filtered diffs for auto-navigation
      const filteredDiffs = getFilteredDiffs();
      const currentIndex = filteredDiffs.findIndex(d => d.id === currentDiffId);

      if (currentIndex !== -1 && currentIndex < filteredDiffs.length - 1) {
        // Found next diff in filtered list
        const nextDiff = filteredDiffs[currentIndex + 1];
        toast.success('All changes processed! Opening next diff...');
        setTimeout(() => {
          // Preserve all filters when navigating to next diff
          const queryString = getQueryString();
          navigate(`/diffs/${nextDiff.id}${queryString ? `?${queryString}` : ''}`);
        }, 1500); // Give user time to see the message
      } else {
        // No more diffs
        toast.success('All changes processed! No more diffs to review.');
        setTimeout(() => {
          // Preserve filters when returning to main page
          const queryString = getQueryString();
          navigate(`/diffs${queryString ? `?${queryString}` : ''}`);
        }, 2000);
      }
    } catch (err) {
      console.error('Error finding next diff:', err);
      toast.success('All changes processed!');
    }
  };

  // Collection handlers
  const handleQuickAddToCollection = async (change: EventChange) => {
    const targetCollectionId = lastUsedCollectionId || collections[0]?.id;

    if (!targetCollectionId) {
      toast.error("No Collection", {
        description: "Please create a collection first",
      });
      return;
    }

    if (!diff || !change.asset_id) return;

    setIsAddingToCollection(true);

    try {
      // Fetch the existing asset to get all the URLs
      const { graphqlClient, updateGraphQLClientAuth } = await import('@/utils/graphqlClient');
      await updateGraphQLClientAuth();

      const ASSET_QUERY = `
        query GetAsset($id: ID!) {
          asset(id: $id) {
            page_id
            slug
            version
            secondary_version
            secondary_page_id
            comparison_mode
            primary_original_url
            primary_cropped_url
            secondary_original_url
            secondary_cropped_url
          }
        }
      `;

      const data = await graphqlClient.request<{ asset: any }>(ASSET_QUERY, { id: change.asset_id });
      const existingAsset = data.asset;

      if (!existingAsset) {
        throw new Error('Could not find asset for this event');
      }


      // For diff type assets, copy ONLY the active URLs from the existing asset (per schema doc)
      const assetInput = {
        collection_id: targetCollectionId,
        type: 'diff' as const,
        page_id: existingAsset.page_id,
        secondary_page_id: existingAsset.secondary_page_id || existingAsset.page_id,
        slug: existingAsset.slug,
        version: existingAsset.version,
        secondary_version: existingAsset.secondary_version,
        title: `${diff.slug} - ${change.event_type} change`,
        description: change.description || `${change.event_type} change for ${diff.slug}`,
        header: diff.hook || undefined, // Use diff hook for header
        subheader: change.description || undefined, // Use event description for subheader
        comparison_mode: existingAsset.comparison_mode || 'side_by_side' as const,
        // Only pass the active URL fields (original and cropped)
        primary_original_url: existingAsset.primary_original_url,
        primary_cropped_url: existingAsset.primary_cropped_url,
        secondary_original_url: existingAsset.secondary_original_url,
        secondary_cropped_url: existingAsset.secondary_cropped_url,
      };


      const response = await createAssetMutation(assetInput);


      if (response.createAsset) {
        // Update last used collection
        if (typeof window !== 'undefined') {
          localStorage.setItem('lastUsedCollection', targetCollectionId);
          setLastUsedCollectionId(targetCollectionId);
        }

        // Invalidate queries
        queryClient.invalidateQueries({ queryKey: ['collections'] });
        queryClient.invalidateQueries({ queryKey: ['assets'] });
        refetchAssets();

        const collection = collections.find(c => c.id === targetCollectionId);
        const assetUrl = `/collections/${targetCollectionId}/${response.createAsset.id}`;

        // Success toast with action button
        toast.success(`Added to ${collection?.name || 'collection'}`, {
          duration: 5000,
          action: {
            label: 'View',
            onClick: (event) => {
              event.preventDefault();
              event.stopPropagation();
              window.location.href = assetUrl;
            },
          },
        });
      }
    } catch (error) {
      console.error('Error adding to collection:', error);
      toast.error("Error", {
        description: "Failed to add to collection",
      });
    } finally {
      setIsAddingToCollection(false);
    }
  };

  const handleAddToSpecificCollection = async (change: EventChange, collectionId: string) => {
    if (!diff || !change.asset_id) return;

    setIsAddingToCollection(true);

    // Set as last used and quick add to this collection
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastUsedCollection', collectionId);
      setLastUsedCollectionId(collectionId);
    }

    try {
      // Fetch the existing asset to get all the URLs
      const { graphqlClient, updateGraphQLClientAuth } = await import('@/utils/graphqlClient');
      await updateGraphQLClientAuth();

      const ASSET_QUERY = `
        query GetAsset($id: ID!) {
          asset(id: $id) {
            page_id
            slug
            version
            secondary_version
            secondary_page_id
            comparison_mode
            primary_original_url
            primary_cropped_url
            secondary_original_url
            secondary_cropped_url
          }
        }
      `;

      const data = await graphqlClient.request<{ asset: any }>(ASSET_QUERY, { id: change.asset_id });
      const existingAsset = data.asset;

      if (!existingAsset) {
        throw new Error('Could not find asset for this event');
      }


      // For diff type assets, copy ONLY the active URLs from the existing asset (per schema doc)
      const assetInput = {
        collection_id: collectionId,
        type: 'diff' as const,
        page_id: existingAsset.page_id,
        secondary_page_id: existingAsset.secondary_page_id || existingAsset.page_id,
        slug: existingAsset.slug,
        version: existingAsset.version,
        secondary_version: existingAsset.secondary_version,
        title: `${diff.slug} - ${change.event_type} change`,
        description: change.description || `${change.event_type} change for ${diff.slug}`,
        header: diff.hook || undefined, // Use diff hook for header
        subheader: change.description || undefined, // Use event description for subheader
        comparison_mode: existingAsset.comparison_mode || 'side_by_side' as const,
        // Only pass the active URL fields (original and cropped)
        primary_original_url: existingAsset.primary_original_url,
        primary_cropped_url: existingAsset.primary_cropped_url,
        secondary_original_url: existingAsset.secondary_original_url,
        secondary_cropped_url: existingAsset.secondary_cropped_url,
      };


      const response = await createAssetMutation(assetInput);


      if (response.createAsset) {
        queryClient.invalidateQueries({ queryKey: ['collections'] });
        queryClient.invalidateQueries({ queryKey: ['assets'] });
        refetchAssets();

        const collection = collections.find(c => c.id === collectionId);
        const assetUrl = `/collections/${collectionId}/${response.createAsset.id}`;

        // Success toast with action button
        toast.success(`Added to ${collection?.name}`, {
          duration: 5000,
          action: {
            label: 'View',
            onClick: (event) => {
              event.preventDefault();
              event.stopPropagation();
              window.location.href = assetUrl;
            },
          },
        });
      }
    } catch (error) {
      toast.error("Error", {
        description: "Failed to add to collection",
      });
    } finally {
      setIsAddingToCollection(false);
    }
  };


  const handleClose = () => {
    setIsOpen(false);
    const queryString = getQueryString();
    setTimeout(() => navigate(`/diffs${queryString ? `?${queryString}` : ''}#diff-${id}`), 150); // Small delay for smooth transition
  };

  const handleVerifyEvent = async (eventId: string, currentVerified: boolean) => {
    mutations.verifyEvent(
      { eventId, verified: !currentVerified },
      {
        onSuccess: () => {
          // Auto-navigate logic for verification
          if (!currentVerified && showFilter === 'unverified' && diff) {
            // When verifying in unverified filter, check if any relevant unverified changes remain
            const updatedChanges = diff.changes.map(change =>
              change.id === eventId
                ? { ...change, verified: !currentVerified, invalid: !currentVerified ? false : change.invalid }
                : change
            );

            const newFilteredChanges = updatedChanges
              .filter(change => {
                // Apply unverified filter
                if (change.verified) return false;

                // Apply event type filter from URL if present
                if (eventTypeFromUrl && eventTypeFromUrl !== 'all') {
                  if (change.event_type !== eventTypeFromUrl) return false;
                }

                return true;
              });

            if (newFilteredChanges.length === 0) {
              // No more relevant unverified changes - check if we should open next diff
              checkForNextDiff(diff.id);
            }
          }

          toast.success(`Event ${!currentVerified ? 'verified' : 'unverified'}`);
        },
        onError: (err: any) => {
          console.error('Error updating event verification:', err);
          toast.error('Failed to update verification status');
        }
      }
    );
  };

  const handlePartiallyCorrectEvent = async (eventId: string, currentPartiallyCorrect: boolean) => {
    mutations.partiallyCorrectEvent(
      { eventId, partiallyCorrect: !currentPartiallyCorrect },
      {
        onSuccess: () => {
          // Auto-navigate logic for partially correct (same as verify behavior)
          if (!currentPartiallyCorrect && showFilter === 'unverified' && diff) {
            const updatedChanges = diff.changes.map(change =>
              change.id === eventId
                ? { ...change, partially_correct: !currentPartiallyCorrect }
                : change
            );

            const newFilteredChanges = updatedChanges
              .filter(change => {
                // Apply unverified filter (both unverified AND not partially correct)
                if (change.verified || change.partially_correct) return false;

                // Apply event type filter from URL if present
                if (eventTypeFromUrl && eventTypeFromUrl !== 'all') {
                  if (change.event_type !== eventTypeFromUrl) return false;
                }

                return true;
              });

            if (newFilteredChanges.length === 0) {
              // No more relevant unverified changes - check if we should open next diff
              checkForNextDiff(diff.id);
            }
          }

          toast.success(`Event ${!currentPartiallyCorrect ? 'marked as partially correct' : 'unmarked as partially correct'}`);
        },
        onError: (err: any) => {
          console.error('Error updating event partially correct status:', err);
          toast.error('Failed to update partially correct status');
        }
      }
    );
  };

  const handleInvalidEvent = async (eventId: string, currentInvalid: boolean) => {
    mutations.invalidEvent(
      { eventId, invalid: !currentInvalid },
      {
        onSuccess: () => {
          // Auto-navigate logic for invalid (same as verify behavior)
          if (!currentInvalid && showFilter === 'unverified' && diff) {
            const updatedChanges = diff.changes.map(change =>
              change.id === eventId
                ? { ...change, invalid: !currentInvalid, verified: !currentInvalid ? false : change.verified }
                : change
            );

            const newFilteredChanges = updatedChanges
              .filter(change => {
                // Apply unverified filter (both unverified AND not partially correct AND not invalid)
                if (change.verified || change.partially_correct || change.invalid) return false;

                // Apply event type filter if specified
                if (eventTypeFromUrl && eventTypeFromUrl !== 'all') {
                  return change.event_type === eventTypeFromUrl;
                }
                return true;
              });

            // If no more relevant unverified changes, navigate to next diff automatically
            if (newFilteredChanges.length === 0 && allDiffs.length > 1) {
              const currentDiffIndex = allDiffs.findIndex(d => d.id === diff.id);
              let nextDiffIndex = -1;

              // Find next diff with unverified changes
              for (let i = currentDiffIndex + 1; i < allDiffs.length; i++) {
                const nextDiff = allDiffs[i];
                const hasUnverified = nextDiff.changes.some(change => {
                  if (change.verified || change.partially_correct || change.invalid) return false;
                  if (eventTypeFromUrl && eventTypeFromUrl !== 'all') {
                    return change.event_type === eventTypeFromUrl;
                  }
                  return true;
                });
                if (hasUnverified) {
                  nextDiffIndex = i;
                  break;
                }
              }

              if (nextDiffIndex !== -1) {
                const nextDiff = allDiffs[nextDiffIndex];
                const queryString = getQueryString();
                navigate(`/diffs/${nextDiff.id}${queryString ? `?${queryString}` : ''}`);
              }
            }
          }

          toast.success(`Event marked as ${!currentInvalid ? 'invalid' : 'valid'}`);
        },
        onError: (error: any) => {
          console.error('Error updating invalid status:', error);
          toast.error('Failed to update invalid status');
        }
      }
    );
  };

  const handleEventTypeChange = async (eventId: string, newEventType: 'price' | 'plan' | 'feature' | 'discount' | 'metric' | 'other') => {
    mutations.updateEventType(
      { eventId, eventType: newEventType },
      {
        onSuccess: () => {
          toast.success(`Event type updated to ${newEventType}`);
        },
        onError: (err: any) => {
          console.error('Error updating event type:', err);
          toast.error('Failed to update event type');
        }
      }
    );
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('Are you sure you want to delete this event?')) return;

    mutations.deleteEvent(eventId, {
      onSuccess: () => {
        // Auto-navigate logic for deletion
        if (diff) {
          const newChanges = diff.changes.filter(change => change.id !== eventId);
          const newFilteredChanges = newChanges
            .filter(change => {
              // Apply unverified filter if active
              if (showFilter === 'unverified' && change.verified) return false;

              // Apply event type filter from URL if present
              if (eventTypeFromUrl && eventTypeFromUrl !== 'all') {
                if (change.event_type !== eventTypeFromUrl) return false;
              }

              return true;
            });

          if (newFilteredChanges.length === 0) {
            // No more relevant changes - check if we should open next diff
            checkForNextDiff(diff.id);
          }
        }

        toast.success('Event deleted successfully');
      },
      onError: (err: any) => {
        console.error('Error deleting event:', err);
        toast.error('Failed to delete event');
      }
    });
  };

  const handleCloneEvent = async (eventId: string) => {
    try {
      await mutations.cloneEvent(eventId);
      toast.success('Event and asset cloned successfully');
    } catch (err: any) {
      console.error('Error cloning event:', err);
      toast.error('Failed to clone event');
    }
  };

  const handleVerifyAll = async () => {
    if (!diff) return;

    setVerifyingAll(true);
    try {
      const unverifiedEvents = diff.changes.filter(change => !change.verified);

      // Use the imported functions directly for bulk operations
      await Promise.all(
        unverifiedEvents.map(change => updateEventVerified(change.id, true))
      );

      // Check if we should open next diff after verify all
      if (showFilter === 'unverified') {
        // Since all events are now verified, check for next diff
        checkForNextDiff(diff.id);
      }

      toast.success(`Verified ${unverifiedEvents.length} events`);
    } catch (err) {
      console.error('Error verifying all events:', err);
      toast.error('Failed to verify all events');
    } finally {
      setVerifyingAll(false);
    }
  };

  const handleStartEdit = (eventId: string, currentDescription: string) => {
    setEditingEventId(eventId);
    setEditedDescription(currentDescription || '');
  };

  const handleSaveDescription = async (eventId: string) => {
    try {
      await mutations.updateEventDescription({ eventId, description: editedDescription });
      setEditingEventId(null);
      setEditedDescription('');
      toast.success('Description updated');
    } catch (error) {
      console.error('Error updating description:', error);
      toast.error('Failed to update description');
    }
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setEditedDescription('');
  };

  const handleOpenMergeModal = (targetEvent: EventChange) => {
    setTargetEventForMerge(targetEvent);
    setSelectedSourceEventId(null);
    setMergeModalOpen(true);
  };

  const handleCloseMergeModal = () => {
    setMergeModalOpen(false);
    setTargetEventForMerge(null);
    setSelectedSourceEventId(null);
  };

  const handleConfirmMerge = async () => {
    if (!targetEventForMerge || !selectedSourceEventId) {
      toast.error('Please select an event to merge into this one');
      return;
    }

    try {
      await mutations.mergeEvents({
        sourceEventId: selectedSourceEventId,
        targetEventId: targetEventForMerge.id
      });
      handleCloseMergeModal();
      toast.success('Events merged successfully');
    } catch (error) {
      console.error('Error merging events:', error);
      toast.error('Failed to merge events');
    }
  };

  // Get compatible events for merging (same slug, different id)
  const getCompatibleEvents = (targetEvent: EventChange) => {
    if (!diff) return [];
    return diff.changes.filter(
      change => change.id !== targetEvent.id && change.slug === targetEvent.slug
    );
  };

  // Preview merged description
  const previewMergedDescription = (targetEvent: EventChange, sourceEventId: string) => {
    const sourceEvent = diff?.changes.find(c => c.id === sourceEventId);
    if (!sourceEvent) return '';

    const targetDesc = targetEvent.description || '';
    const sourceDesc = sourceEvent.description || '';

    if (targetDesc && sourceDesc) {
      return targetDesc + ' ; ' + sourceDesc;
    } else if (sourceDesc) {
      return sourceDesc;
    } else {
      return targetDesc;
    }
  };

  // Get or set default image type for an event
  const getEventImageType = (eventId: string) => {
    return eventImageTypes[eventId] || 'cropped';
  };

  const setEventImageType = (eventId: string, imageType: 'markup' | 'cropped' | 'original') => {
    setEventImageTypes(prev => ({ ...prev, [eventId]: imageType }));
  };

  // Handler for toggling published status
  const handleTogglePublished = async () => {
    if (!diff) return;

    try {
      await setDiffPublished(diff.id, !diff.published);

      // Refetch the diff to get the updated published status
      refetchDiff();

      toast.success(`Diff ${!diff.published ? 'published' : 'unpublished'} successfully`);
    } catch (error) {
      console.error('Error toggling published status:', error);
      toast.error('Failed to toggle published status');
    }
  };

  // Handlers for diff field editing
  const handleStartDiffEdit = (field: 'hook' | 'description', currentValue: string) => {
    setEditingDiffField(field);
    setEditedDiffValue(currentValue || '');
  };

  const handleSaveDiffField = async () => {
    if (!diff || !editingDiffField) return;

    try {
      await updateDiff(diff.id, { [editingDiffField]: editedDiffValue });
      setEditingDiffField(null);
      setEditedDiffValue('');
      toast.success(`${editingDiffField === 'hook' ? 'Hook' : 'Description'} updated`);
      // Refetch the diff to show updated values
      refetchDiff();
    } catch (error) {
      console.error('Error updating diff field:', error);
      toast.error(`Failed to update ${editingDiffField}`);
    }
  };

  const handleCancelDiffEdit = () => {
    setEditingDiffField(null);
    setEditedDiffValue('');
  };

  // AI Helper handlers
  const handleOpenAiHelper = (field: 'hook' | 'description') => {
    setAiHelperOpen(field);
    setAiCustomInstructions('');
  };

  const handleCloseAiHelper = () => {
    setAiHelperOpen(null);
    setAiCustomInstructions('');
  };

  const handleGenerateFromAI = async (generateFromEvents: boolean) => {
    if (!diff || !aiHelperOpen) return;

    setAiGenerating(true);
    try {
      // Build the context from events if generateFromEvents is true
      let contextText = '';
      if (generateFromEvents && diff.changes && diff.changes.length > 0) {
        contextText = diff.changes.map(e => e.description).join(', ');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          field: aiHelperOpen,
          context: generateFromEvents ? contextText : undefined,
          customInstructions: aiCustomInstructions || undefined,
          slug: diff.slug,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('AI Writer error response:', errorData);
        throw new Error(errorData.error || 'Failed to generate content');
      }

      const data = await response.json();

      // Set the generated text in the edit field
      setEditingDiffField(aiHelperOpen);
      setEditedDiffValue(data.text);
      handleCloseAiHelper();
      toast.success('Content generated successfully');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate content');
    } finally {
      setAiGenerating(false);
    }
  };

  // Event AI Helper handlers
  const handleOpenEventAiHelper = (eventId: string) => {
    setEventAiHelperOpen(eventId);
    setEventAiCustomInstructions('');
  };

  const handleCloseEventAiHelper = () => {
    setEventAiHelperOpen(null);
    setEventAiCustomInstructions('');
  };

  const handleGenerateEventAI = async (eventId: string, currentDescription: string, isRewrite: boolean = false) => {
    if (!diff) return;

    setEventAiGenerating(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          field: 'event_description',
          context: isRewrite ? currentDescription : (eventAiCustomInstructions || currentDescription),
          slug: diff.slug,
          isRewrite: isRewrite,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('AI Writer error response:', errorData);
        throw new Error(errorData.error || 'Failed to generate content');
      }

      const data = await response.json();

      // Set the generated text in the edit field
      setEditingEventId(eventId);
      setEditedDescription(data.text);
      handleCloseEventAiHelper();
      toast.success('Content generated successfully');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate content');
    } finally {
      setEventAiGenerating(false);
    }
  };

  // Handler to open side-by-side editor
  const handleOpenSideBySideEditor = async (changeId: string) => {
    // Find the specific change/event
    const change = diff?.changes.find(c => c.id === changeId);
    
    if (!change?.asset_id) {
      toast.error('No asset found for this event');
      return;
    }

    // Get the current imageType being displayed
    const imageType = getEventImageType(changeId);

    try {
      // Fetch the asset for THIS SPECIFIC EVENT
      const { graphqlClient, updateGraphQLClientAuth } = await import('@/utils/graphqlClient');
      await updateGraphQLClientAuth();

      const ASSET_QUERY = `
        query GetAsset($id: ID!) {
          asset(id: $id) {
            id
            slug
            primary_cropped_url
            primary_markup_url
            primary_original_url
            primary_image
            secondary_cropped_url
            secondary_markup_url
            secondary_original_url
            secondary_image
            version
            secondary_version
            comparison_mode
          }
        }
      `;

      const data = await graphqlClient.request<{ asset: any }>(ASSET_QUERY, { id: change.asset_id });
      const asset = data.asset;

      if (!asset) {
        toast.error('Asset not found');
        return;
      }

      const comparisonMode = asset.comparison_mode || 'side_by_side';

      // Helper function to get URL based on image type
      const getUrl = (prefix: 'primary' | 'secondary') => {
        switch (imageType) {
          case 'markup':
            return asset[`${prefix}_markup_url`] || asset[`${prefix}_cropped_url`] || asset[`${prefix}_original_url`] || asset[`${prefix}_image`];
          case 'cropped':
            return asset[`${prefix}_cropped_url`] || asset[`${prefix}_original_url`] || asset[`${prefix}_image`];
          case 'original':
            // Try original URL first, then strip transformations from cropped/markup URLs, finally fallback to primary_image
            return asset[`${prefix}_original_url`] ||
                   stripCloudinaryTransformations(asset[`${prefix}_cropped_url`]) ||
                   stripCloudinaryTransformations(asset[`${prefix}_markup_url`]) ||
                   asset[`${prefix}_image`];
          default:
            return asset[`${prefix}_cropped_url`] || asset[`${prefix}_original_url`] || asset[`${prefix}_image`];
        }
      };

      if (comparisonMode === 'single') {
        // Single mode: only edit the after/secondary image
        const url2 = asset.secondary_cropped_url || getUrl('secondary');

        if (!url2) {
          toast.error('After image is required for editing');
          return;
        }

        // Open editor with only the after image (pass same URL for both to maintain compatibility)
        setCurrentEditingAssetId(change.asset_id);
        setEditorUrls({ url1: url2, url2 });
        setSideBySideEditorOpen(true);
      } else {
        // Side-by-side mode: edit both images
        let url1 = asset.primary_cropped_url || getUrl('primary');
        let url2 = asset.secondary_cropped_url || getUrl('secondary');

        if (!url1 || !url2) {
          toast.error('Both images are required for side-by-side editing');
          return;
        }

        // Open editor with both images
        setCurrentEditingAssetId(change.asset_id);
        setEditorUrls({ url1, url2 });
        setSideBySideEditorOpen(true);
      }
    } catch (error) {
      console.error('Error fetching asset:', error);
      toast.error('Failed to load asset images');
    }
  };

  // Handler to save edited images from side-by-side editor
  const handleSaveSideBySideImages = async (url1: string, url2: string) => {
    if (!currentEditingAssetId) {
      toast.error('No asset selected for editing');
      return;
    }

    try {
      // Update the asset with the new cropped URLs
      await updateAsset(currentEditingAssetId, {
        primary_cropped_url: url1,
        secondary_cropped_url: url2,
      });

      toast.success('Images updated successfully');

      // Close editor and clear state
      setSideBySideEditorOpen(false);
      setEditorUrls(null);
      setCurrentEditingAssetId(null);

      // Force a refresh of the asset viewer
      setAssetRefreshKey(prev => prev + 1);

      // Refetch the diff data
      refetchDiff();
    } catch (error) {
      console.error('Error saving images:', error);
      toast.error('Failed to save images');
    }
  };

  // Handler to cancel side-by-side editor
  const handleCancelSideBySideEditor = () => {
    setSideBySideEditorOpen(false);
    setEditorUrls(null);
    setCurrentEditingAssetId(null);
  };

  // Handler to toggle comparison mode
  const handleToggleComparisonMode = async (assetId: string, newMode: 'single' | 'side_by_side') => {
    try {
      await updateAsset(assetId, { comparison_mode: newMode });
      toast.success(`View mode updated to ${newMode === 'single' ? 'single image' : 'side-by-side'}`);
      // Force a refresh of asset components
      setAssetRefreshKey(prev => prev + 1);
      refetchDiff();
    } catch (error) {
      console.error('Error updating comparison mode:', error);
      toast.error('Failed to update view mode');
    }
  };

  // Handler to open lightbox when image is clicked
  const handleImageClick = (assetId: string, image: 'before' | 'after') => {
    setLightboxState({
      isOpen: true,
      assetId,
      currentImage: image
    });
  };

  // Handler to close lightbox
  const handleCloseLightbox = () => {
    setLightboxState({
      isOpen: false,
      assetId: null,
      currentImage: 'after'
    });
  };

  // Handler to navigate between images in lightbox
  const handleNavigateLightbox = (image: 'before' | 'after') => {
    setLightboxState(prev => ({
      ...prev,
      currentImage: image
    }));
  };

  // Handler to delete diff
  const handleDeleteDiff = async () => {
    if (!diff) return;

    setIsDeleting(true);
    try {
      const result = await deleteDiff(diff.id);

      if (result.deleteDiff.success) {
        toast.success('Diff deleted successfully');

        // Try to navigate to next diff, or close modal if none available
        const filteredDiffs = getFilteredDiffs();
        const currentIndex = filteredDiffs.findIndex(d => d.id === diff.id);

        if (currentIndex < filteredDiffs.length - 1) {
          // Navigate to next diff
          const nextDiff = filteredDiffs[currentIndex + 1];
          const queryString = getQueryString();
          navigate(`/diffs/${nextDiff.id}${queryString ? `?${queryString}` : ''}`);
        } else if (currentIndex > 0) {
          // No next diff, navigate to previous
          const prevDiff = filteredDiffs[currentIndex - 1];
          const queryString = getQueryString();
          navigate(`/diffs/${prevDiff.id}${queryString ? `?${queryString}` : ''}`);
        } else {
          // No other diffs, close modal and go back to list
          const queryString = getQueryString();
          navigate(`/diffs${queryString ? `?${queryString}` : ''}`);
        }
      } else {
        toast.error(result.deleteDiff.message || 'Failed to delete diff');
      }
    } catch (error) {
      console.error('Error deleting diff:', error);
      toast.error('Failed to delete diff');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <p className="text-gray-600">Loading diff details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !diff) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="py-8">
            <div className="mb-6">
              <Button variant="ghost" onClick={() => {
                const queryString = getQueryString();
                navigate(`/diffs${queryString ? `?${queryString}` : ''}`);
              }} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Diffs
              </Button>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error || 'Diff not found'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const unverifiedCount = diff.changes.filter(change => !change.verified).length;

  // Define event type order priority (lower number = higher priority)
  const getEventTypePriority = (eventType?: string): number => {
    const priorities: Record<string, number> = {
      'price': 1,
      'plan': 2,
      'feature': 3,
      'discount': 4,
      'metric': 5,
      'other': 6
    };
    return priorities[eventType || 'other'] || 6;
  };

  // Filter and sort changes based on current filter setting and event type from URL
  const filteredChanges = diff.changes
    .filter(change => {
      // Apply unverified filter
      if (showFilter === 'unverified' && change.verified) {
        return false;
      }

      // Apply event type filter from URL
      if (eventTypeFromUrl && eventTypeFromUrl !== 'all') {
        if (change.event_type !== eventTypeFromUrl) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      // Sort by event type priority (other types last)
      return getEventTypePriority(a.event_type) - getEventTypePriority(b.event_type);
    });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent
        className="max-w-none w-screen h-screen p-0 bg-gray-50 border-none overflow-hidden"
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 z-10">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button variant="ghost" onClick={handleClose} className="flex items-center gap-2">
                    <X className="h-4 w-4" />
                    Close
                  </Button>

                  {/* Diff Navigation */}
                  {(() => {
                    const filteredDiffs = getFilteredDiffs();
                    const position = getCurrentDiffPosition();
                    const currentIndex = diff ? filteredDiffs.findIndex(d => d.id === diff.id) : -1;
                    const allDiffsLoaded = filteredDiffs.length >= totalCount;

                    return position.total > 0 && (
                      <>
                        <div className="border-l border-gray-300 h-6"></div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={goToPreviousDiff}
                            disabled={!diff || currentIndex <= 0}
                            className="flex items-center gap-1"
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Prev Diff
                          </Button>
                          <span className="text-sm text-gray-600">
                            {position.current > 0 ? position.current : '?'} of {position.total}
                            {!allDiffsLoaded && (
                              <Loader2 className="inline h-3 w-3 ml-1 animate-spin text-gray-400" />
                            )}
                            {(() => {
                              const activeFilters = [];
                              if (slugFromUrl) activeFilters.push(`company: ${slugFromUrl}`);
                              if (quarterFromUrl && quarterFromUrl !== 'all') activeFilters.push(quarterFromUrl);
                              if (eventTypeFromUrl && eventTypeFromUrl !== 'all') activeFilters.push(eventTypeFromUrl);
                              if (verificationFromUrl && verificationFromUrl !== 'all') activeFilters.push(verificationFromUrl);
                              if (publishedFromUrl && publishedFromUrl !== 'all') activeFilters.push(publishedFromUrl);
                              if (createdFromUrl && createdFromUrl !== 'all') activeFilters.push(`last ${createdFromUrl} days`);

                              return activeFilters.length > 0 && (
                                <span className="text-blue-600 ml-1">({activeFilters.join(', ')})</span>
                              );
                            })()}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={goToNextDiff}
                            disabled={!diff || currentIndex >= filteredDiffs.length - 1}
                            className="flex items-center gap-1"
                          >
                            Next Diff
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="flex items-center gap-3">
                  {/* Published Toggle Button */}
                  <Button
                    onClick={handleTogglePublished}
                    variant="outline"
                    size="sm"
                    className={`flex items-center gap-2 ${diff.published ? 'text-green-600 border-green-300 hover:bg-green-50' : 'text-gray-500 border-gray-300 hover:bg-gray-50'}`}
                  >
                    {diff.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    {diff.published ? 'Published' : 'Unpublished'}
                  </Button>

                  {/* Preview Button */}
                  <Button
                    onClick={handlePreview}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Preview
                  </Button>

                  {/* Filter Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Show:</span>
                    <ToggleGroup
                      type="single"
                      value={showFilter}
                      onValueChange={(value) => {
                        if (value) {
                          setShowFilter(value as 'unverified' | 'all');
                        }
                      }}
                    >
                      <ToggleGroupItem value="unverified" className="text-xs px-3 py-1 h-7">
                        Unverified ({unverifiedCount})
                      </ToggleGroupItem>
                      <ToggleGroupItem value="all" className="text-xs px-3 py-1 h-7">
                        All ({diff.changes.length})
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>

                  {unverifiedCount > 0 && (
                    <Button
                      onClick={handleVerifyAll}
                      disabled={verifyingAll}
                      className="flex items-center gap-2"
                    >
                      <CheckCheck className="h-4 w-4" />
                      {verifyingAll ? 'Verifying...' : `Verify All (${unverifiedCount})`}
                    </Button>
                  )}

                  {/* Delete Diff Button */}
                  <Button
                    onClick={() => setShowDeleteDialog(true)}
                    variant="destructive"
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Diff
                  </Button>
                </div>
              </div>

            </div>
          </div>

          {/* Scrollable Event List */}
          <div className="flex-1 min-h-0 overflow-y-auto bg-gray-100">
            <div className="max-w-[1800px] mx-auto p-6 pb-96 space-y-6">
              {/* Diff Summary Card */}
              <Card className="bg-white border-gray-300">
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                          <h2 className="text-2xl font-bold text-gray-900">
                            {diff.page?.pricing_page_url ? (
                              <a
                                href={diff.page.pricing_page_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-blue-600 hover:underline transition-colors"
                                title="View pricing page"
                              >
                                {diff.slug}
                              </a>
                            ) : (
                              diff.slug
                            )}
                          </h2>
                          {diff.page?.pricing_page_url && (
                            <svg
                              className="w-5 h-5 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          )}
                        </div>
                        <Badge variant="outline" className="text-sm">
                          {diff.changes.length} {diff.changes.length === 1 ? 'change' : 'changes'}
                        </Badge>
                      </div>
                      <span className="text-sm font-semibold text-gray-600">
                        {formatVersionDate(diff.v_before)} → {formatVersionDate(diff.v_after)}
                      </span>
                    </div>

                    {/* Editable Hook */}
                    <div className="flex items-center justify-center gap-3 relative">
                      {editingDiffField === 'hook' ? (
                        <div className="space-y-2 w-full">
                          <Textarea
                            value={editedDiffValue}
                            onChange={(e) => setEditedDiffValue(e.target.value)}
                            className="text-2xl text-center min-h-[100px] resize-y"
                            placeholder="Enter hook..."
                            autoFocus
                          />
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              onClick={handleSaveDiffField}
                              className="flex items-center gap-2"
                            >
                              <Check className="h-3 w-3" />
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelDiffEdit}
                              className="flex items-center gap-2"
                            >
                              <X className="h-3 w-3" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            onClick={() => handleStartDiffEdit('hook', diff.hook || '')}
                            className="text-2xl text-center text-gray-900 font-semibold cursor-pointer hover:bg-gray-100 rounded p-3 transition-colors min-h-[48px] flex-1"
                            title="Click to edit"
                          >
                            {diff.hook || <span className="text-gray-400 italic">Click to add hook...</span>}
                          </div>

                          <div className="relative">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenAiHelper('hook')}
                              className="flex items-center gap-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              title="AI Writing Assistant"
                            >
                              <Sparkles className="h-4 w-4" />
                              <span className="text-xs">AI</span>
                            </Button>

                            {/* Floating AI Dropdown */}
                            {aiHelperOpen === 'hook' && (
                              <div className="absolute top-full mt-2 right-0 z-50 w-80 bg-white rounded-lg shadow-2xl border-2 border-purple-200">
                                <div className="p-3 space-y-2">
                                  <div className="flex items-center justify-between pb-2 border-b">
                                    <div className="flex items-center gap-2">
                                      <Sparkles className="h-4 w-4 text-purple-600" />
                                      <span className="font-medium text-sm">AI Assistant</span>
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={handleCloseAiHelper} className="h-6 w-6 p-0">
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>

                                  <button
                                    onClick={() => handleGenerateFromAI(true)}
                                    disabled={aiGenerating}
                                    className="w-full p-2 rounded border border-purple-200 bg-purple-50 hover:bg-purple-100 transition-colors disabled:opacity-50 text-left text-sm"
                                  >
                                    {aiGenerating ? (
                                      <div className="flex items-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Generating...
                                      </div>
                                    ) : (
                                      'Generate from Events'
                                    )}
                                  </button>

                                  <div className="text-xs text-center text-gray-400">or</div>

                                  <Textarea
                                    value={aiCustomInstructions}
                                    onChange={(e) => setAiCustomInstructions(e.target.value)}
                                    placeholder="Custom instructions..."
                                    className="text-xs min-h-[50px]"
                                    disabled={aiGenerating}
                                  />

                                  <Button
                                    onClick={() => handleGenerateFromAI(false)}
                                    disabled={aiGenerating || !aiCustomInstructions.trim()}
                                    size="sm"
                                    className="w-full bg-purple-600 hover:bg-purple-700 h-8"
                                  >
                                    {aiGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Generate'}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Editable Description */}
                    <div className="flex items-center justify-center gap-3 relative">
                      {editingDiffField === 'description' ? (
                        <div className="space-y-2 w-full">
                          <Textarea
                            value={editedDiffValue}
                            onChange={(e) => setEditedDiffValue(e.target.value)}
                            className="text-sm text-center min-h-[60px] resize-y"
                            placeholder="Enter description..."
                            autoFocus
                          />
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              onClick={handleSaveDiffField}
                              className="flex items-center gap-2"
                            >
                              <Check className="h-3 w-3" />
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelDiffEdit}
                              className="flex items-center gap-2"
                            >
                              <X className="h-3 w-3" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            onClick={() => handleStartDiffEdit('description', diff.description || '')}
                            className="text-sm text-center text-gray-700 cursor-pointer hover:bg-gray-100 rounded p-2 transition-colors min-h-[32px] flex-1"
                            title="Click to edit"
                          >
                            {diff.description || <span className="text-gray-400 italic">Click to add description...</span>}
                          </div>

                          <div className="relative">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenAiHelper('description')}
                              className="flex items-center gap-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              title="AI Writing Assistant"
                            >
                              <Sparkles className="h-4 w-4" />
                              <span className="text-xs">AI</span>
                            </Button>

                            {/* Floating AI Dropdown */}
                            {aiHelperOpen === 'description' && (
                              <div className="absolute top-full mt-2 right-0 z-50 w-80 bg-white rounded-lg shadow-2xl border-2 border-purple-200">
                                <div className="p-3 space-y-2">
                                  <div className="flex items-center justify-between pb-2 border-b">
                                    <div className="flex items-center gap-2">
                                      <Sparkles className="h-4 w-4 text-purple-600" />
                                      <span className="font-medium text-sm">AI Assistant</span>
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={handleCloseAiHelper} className="h-6 w-6 p-0">
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>

                                  <button
                                    onClick={() => handleGenerateFromAI(true)}
                                    disabled={aiGenerating}
                                    className="w-full p-2 rounded border border-purple-200 bg-purple-50 hover:bg-purple-100 transition-colors disabled:opacity-50 text-left text-sm"
                                  >
                                    {aiGenerating ? (
                                      <div className="flex items-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Generating...
                                      </div>
                                    ) : (
                                      'Generate from Events'
                                    )}
                                  </button>

                                  <div className="text-xs text-center text-gray-400">or</div>

                                  <Textarea
                                    value={aiCustomInstructions}
                                    onChange={(e) => setAiCustomInstructions(e.target.value)}
                                    placeholder="Custom instructions..."
                                    className="text-xs min-h-[50px]"
                                    disabled={aiGenerating}
                                  />

                                  <Button
                                    onClick={() => handleGenerateFromAI(false)}
                                    disabled={aiGenerating || !aiCustomInstructions.trim()}
                                    size="sm"
                                    className="w-full bg-purple-600 hover:bg-purple-700 h-8"
                                  >
                                    {aiGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Generate'}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Change Type Labels */}
                    <div className="flex items-center justify-center gap-2 flex-wrap pt-3 border-t border-gray-200">
                      {getUniqueChangeTypes().map(type => {
                        const config = getChangeTypeConfig(type);
                        const Icon = config.icon;
                        return (
                          <div
                            key={type}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${config.bgColor} ${config.color}`}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="text-sm font-medium">{config.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>


              {filteredChanges.length === 0 ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <div className="text-center text-gray-500">
                    <p className="text-lg">No changes found</p>
                    <p className="text-sm mt-2">Try adjusting your filters</p>
                  </div>
                </div>
              ) : (
                filteredChanges.map((change, index) => (
                  <div key={change.id} className={`bg-white rounded-lg shadow-sm border border-gray-200 mb-8 ${
                    change.verified ? 'border-l-4 border-l-green-500' : ''
                  }`}>
                    {/* Event Header */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-4">
                          {/* Event Type and Status */}
                          <div className="flex items-center gap-3 flex-wrap justify-between">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="text-xs font-medium text-gray-400">
                                Event {index + 1} of {filteredChanges.length}
                              </span>
                              <Select
                                value={change.event_type || 'other'}
                                onValueChange={(value) => handleEventTypeChange(change.id, value as 'price' | 'plan' | 'feature' | 'discount' | 'metric' | 'other')}
                              >
                                <SelectTrigger className="h-8 w-[140px] text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="price">Price</SelectItem>
                                  <SelectItem value="plan">Plan</SelectItem>
                                  <SelectItem value="feature">Feature</SelectItem>
                                  <SelectItem value="discount">Discount</SelectItem>
                                  <SelectItem value="metric">Metric</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <Badge
                                variant={getStatusDisplay(getEventStatus(change)).variant}
                                className={`text-sm ${getEventStatus(change) === 'partially_correct' ? 'bg-yellow-100 text-yellow-800' : ''}`}
                              >
                                {getStatusDisplay(getEventStatus(change)).label}
                              </Badge>
                            </div>

                            {/* Image Type Toggle */}
                            {change.asset_id && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-600">Type:</span>
                                <ToggleGroup
                                  type="single"
                                  value={getEventImageType(change.id)}
                                  onValueChange={(value) => value && setEventImageType(change.id, value as 'markup' | 'cropped' | 'original')}
                                  className="justify-start"
                                >
                                  <ToggleGroupItem value="cropped" className="text-xs px-2 py-1 h-6">
                                    Cropped
                                  </ToggleGroupItem>
                                  <ToggleGroupItem value="original" className="text-xs px-2 py-1 h-6">
                                    Original
                                  </ToggleGroupItem>
                                </ToggleGroup>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex-shrink-0 flex items-start gap-2 flex-wrap justify-end">
                          <Button
                            size="sm"
                            variant={change.verified ? "outline" : "default"}
                            onClick={() => handleVerifyEvent(change.id, change.verified || false)}
                            className="flex items-center gap-2"
                          >
                            <Check className="h-4 w-4" />
                            {change.verified ? 'Unverify' : 'Verify'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenMergeModal(change)}
                            className="flex items-center gap-2"
                          >
                            <GitMerge className="h-4 w-4" />
                            Merge
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCloneEvent(change.id)}
                            className="flex items-center gap-2"
                          >
                            <Copy className="h-4 w-4" />
                            Clone
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteEvent(change.id)}
                            className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                          {!change.verified && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex items-center gap-1 h-8 px-2"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => handlePartiallyCorrectEvent(change.id, change.partially_correct || false)}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <AlertTriangle className="h-4 w-4" />
                                  {change.partially_correct ? 'Unmark' : 'Mark'} Partial
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleInvalidEvent(change.id, change.invalid || false)}
                                  className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600"
                                >
                                  <X className="h-4 w-4" />
                                  {change.invalid ? 'Unmark' : 'Mark'} Invalid
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Images Section */}
                    {change.asset_id ? (
                      <div className="flex flex-col">
                        {/* Controls Bar */}
                        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                          {/* View Mode Toggle */}
                          <ComparisonModeToggle
                            key={`toggle-${change.asset_id}-${assetRefreshKey}`}
                            assetId={change.asset_id}
                            onToggle={handleToggleComparisonMode}
                            refreshKey={assetRefreshKey}
                          />

                          {/* Right side buttons */}
                          <div className="flex items-center gap-2">
                            {/* Add to Collection Split Button */}
                            <div className="flex items-center gap-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickAddToCollection(change)}
                                className="flex items-center gap-2 relative rounded-r-none border-r-0"
                                disabled={collections.length === 0 || isAddingToCollection}
                              >
                                {isAddingToCollection ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Plus className="h-4 w-4" />
                                )}
                                {lastUsedCollectionId && collections.find(c => c.id === lastUsedCollectionId)
                                  ? collections.find(c => c.id === lastUsedCollectionId)?.name
                                  : collections[0]?.name || 'Add to Collection'}
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="px-2 rounded-l-none"
                                    disabled={collections.length === 0 || isAddingToCollection}
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {collections.map((collection) => (
                                    <DropdownMenuItem
                                      key={collection.id}
                                      disabled={isAddingToCollection}
                                      onClick={() => handleAddToSpecificCollection(change, collection.id)}
                                    >
                                      {collection.name}
                                      {collection.id === lastUsedCollectionId && (
                                        <span className="ml-2 text-xs text-gray-500">(Recent)</span>
                                      )}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>

                            {/* Edit Images Button */}
                            <Button
                              onClick={() => handleOpenSideBySideEditor(change.id)}
                              variant="default"
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              <Images className="h-4 w-4" />
                              Edit Images
                            </Button>
                          </div>
                        </div>

                        <div className="h-[600px]">
                          <EmbeddedAssetViewer
                            key={`viewer-${change.asset_id}-${assetRefreshKey}`}
                            assetId={change.asset_id}
                            imageType={getEventImageType(change.id)}
                            onImageTypeChange={(newType) => setEventImageType(change.id, newType)}
                            hideTypeToggle={true}
                            hideEditButtons={true}
                            onImageClick={(image) => handleImageClick(change.asset_id, image)}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="h-[400px] flex items-center justify-center text-gray-500 bg-gray-50">
                        <div className="text-center">
                          <p className="text-lg">No images available for this change</p>
                          <p className="text-sm text-gray-400 mt-2">Asset ID: {change.asset_id || 'Not provided'}</p>
                        </div>
                      </div>
                    )}

                    {/* Description Section - Below Images */}
                    <div className="px-6 py-4 bg-white border-t border-gray-200">
                      <div className="flex items-start gap-3 relative">
                        {editingEventId === change.id ? (
                          <div className="space-y-2 flex-1">
                            <Textarea
                              value={editedDescription}
                              onChange={(e) => setEditedDescription(e.target.value)}
                              className="text-sm italic min-h-[60px] resize-y text-center"
                              placeholder="Enter description..."
                              autoFocus
                            />
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleSaveDescription(change.id)}
                                className="flex items-center gap-2"
                              >
                                <Check className="h-3 w-3" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                                className="flex items-center gap-2"
                              >
                                <X className="h-3 w-3" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div
                              onClick={() => handleStartEdit(change.id, change.description || '')}
                              className="text-sm text-gray-700 italic cursor-pointer hover:bg-gray-50 rounded p-2 transition-colors min-h-[32px] flex-1 text-center"
                              title="Click to edit"
                            >
                              {change.description || <span className="text-gray-400">Click to add description...</span>}
                            </div>

                            <div className="relative flex-shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenEventAiHelper(change.id)}
                                className="flex items-center gap-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                title="AI Writing Assistant"
                              >
                                <Sparkles className="h-4 w-4" />
                                <span className="text-xs">AI</span>
                              </Button>

                              {/* Floating AI Dropdown */}
                              {eventAiHelperOpen === change.id && (
                                <div className="absolute top-full mt-2 right-0 z-[9999] w-80 bg-white rounded-lg shadow-2xl border-2 border-purple-200">
                                  <div className="p-3 space-y-2">
                                    <div className="flex items-center justify-between pb-2 border-b">
                                      <div className="flex items-center gap-2">
                                        <Sparkles className="h-4 w-4 text-purple-600" />
                                        <span className="font-medium text-sm">AI Assistant</span>
                                      </div>
                                      <Button size="sm" variant="ghost" onClick={handleCloseEventAiHelper} className="h-6 w-6 p-0">
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>

                                    <button
                                      onClick={() => handleGenerateEventAI(change.id, change.description || '', true)}
                                      disabled={eventAiGenerating || !change.description}
                                      className="w-full p-2 rounded border border-purple-200 bg-purple-50 hover:bg-purple-100 transition-colors disabled:opacity-50 text-left text-sm"
                                    >
                                      {eventAiGenerating ? (
                                        <div className="flex items-center gap-2">
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                          Generating...
                                        </div>
                                      ) : (
                                        'Rewrite'
                                      )}
                                    </button>

                                    <div className="text-xs text-center text-gray-400">or</div>

                                    <Textarea
                                      value={eventAiCustomInstructions}
                                      onChange={(e) => setEventAiCustomInstructions(e.target.value)}
                                      placeholder="Describe what you want to capture in the image..."
                                      className="text-xs min-h-[60px]"
                                      disabled={eventAiGenerating}
                                    />

                                    <Button
                                      onClick={() => handleGenerateEventAI(change.id, change.description || '', false)}
                                      disabled={eventAiGenerating || !eventAiCustomInstructions.trim()}
                                      size="sm"
                                      className="w-full bg-purple-600 hover:bg-purple-700 h-8"
                                    >
                                      {eventAiGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Generate Caption'}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Side-by-Side Image Editor Modal */}
      <Dialog open={sideBySideEditorOpen} onOpenChange={setSideBySideEditorOpen}>
        <DialogContent className="max-w-[95vw] h-[90vh] p-0">
          <div className="h-full flex flex-col">
            {editorUrls && (
              <SideBySideEditor
                url1={editorUrls.url1}
                url2={editorUrls.url2}
                onSave={handleSaveSideBySideImages}
                onCancel={handleCancelSideBySideEditor}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge Events Modal */}
      <Dialog open={mergeModalOpen} onOpenChange={setMergeModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Merge Event Into This One</DialogTitle>
            <DialogDescription>
              Select another event to merge into this one. The selected event will be deleted and its data merged here.
            </DialogDescription>
          </DialogHeader>

          {targetEventForMerge && (
            <div className="space-y-6">
              {/* Target Event (Keeping) */}
              <div>
                <h3 className="font-semibold text-base text-green-700 mb-3 flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  This Event (will be kept and receive merged data)
                </h3>
                <Card className="bg-green-50 border-green-300 border-2">
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-green-600">{targetEventForMerge.event_type || 'other'}</Badge>
                        <span className="text-sm font-medium text-gray-700">Slug: {targetEventForMerge.slug}</span>
                      </div>
                      <div className="bg-white p-3 rounded border border-green-200">
                        <p className="text-sm font-medium text-gray-900">
                          {targetEventForMerge.description || <span className="text-gray-400 italic">No description</span>}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Source Event Selection */}
              <div>
                <h3 className="font-semibold text-base text-red-700 mb-3 flex items-center gap-2">
                  <X className="h-5 w-5" />
                  Select Event to Merge (will be deleted after merge)
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  {getCompatibleEvents(targetEventForMerge).length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="p-8">
                        <p className="text-sm text-gray-500 text-center">
                          No other events found with the same slug to merge
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    getCompatibleEvents(targetEventForMerge).map((sourceEvent, index) => (
                      <Card
                        key={sourceEvent.id}
                        className={`cursor-pointer transition-all ${
                          selectedSourceEventId === sourceEvent.id
                            ? 'bg-red-50 border-red-400 border-2 shadow-md'
                            : 'hover:bg-gray-50 hover:shadow-sm border-gray-200'
                        }`}
                        onClick={() => setSelectedSourceEventId(sourceEvent.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                              selectedSourceEventId === sourceEvent.id
                                ? 'bg-red-600 text-white'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {selectedSourceEventId === sourceEvent.id ? '✓' : index + 1}
                            </div>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline">{sourceEvent.event_type || 'other'}</Badge>
                                <span className="text-xs text-gray-500">Slug: {sourceEvent.slug}</span>
                              </div>
                              <p className="text-sm text-gray-700">
                                {sourceEvent.description || <span className="text-gray-400 italic">No description</span>}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>

              {/* Preview Merged Result */}
              {selectedSourceEventId && (
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-base text-blue-700 mb-3 flex items-center gap-2">
                    <GitMerge className="h-5 w-5" />
                    Preview: Result After Merge
                  </h3>
                  <Card className="bg-blue-50 border-blue-300 border-2">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-semibold text-blue-900 uppercase mb-2">Merged Description:</p>
                          <div className="bg-white p-3 rounded border border-blue-200">
                            <p className="text-sm whitespace-pre-wrap text-gray-900">
                              {previewMergedDescription(targetEventForMerge, selectedSourceEventId)}
                            </p>
                          </div>
                        </div>
                        <div className="bg-blue-100 p-3 rounded">
                          <p className="text-xs text-blue-800">
                            <strong>Note:</strong> Extracts (before_extracts & after_extract) will also be merged.
                            If both events have the same extract keys, this event's values will be kept.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseMergeModal}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmMerge}
              disabled={!selectedSourceEventId}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              <GitMerge className="h-4 w-4 mr-2" />
              Merge Into This Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      {lightboxState.isOpen && lightboxState.assetId && diff && (
        <LightboxWithAsset
          assetId={lightboxState.assetId}
          currentImage={lightboxState.currentImage}
          imageType={diff.changes.find(c => c.asset_id === lightboxState.assetId)?.id
            ? getEventImageType(diff.changes.find(c => c.asset_id === lightboxState.assetId)!.id)
            : 'cropped'}
          description={diff.changes.find(c => c.asset_id === lightboxState.assetId)?.description}
          onClose={handleCloseLightbox}
          onNavigate={handleNavigateLightbox}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this diff?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the diff for <strong>{diff?.slug}</strong>
              {' '}(from {diff?.v_before ? formatVersionDate(diff.v_before) : 'unknown'} to{' '}
              {diff?.v_after ? formatVersionDate(diff.v_after) : 'unknown'}).
              {diff?.changes && diff.changes.length > 0 && (
                <>
                  <br /><br />
                  This will also delete <strong>{diff.changes.length} associated event{diff.changes.length !== 1 ? 's' : ''}</strong>.
                </>
              )}
              <br /><br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDiff}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

// Helper component to fetch asset and render lightbox
const LightboxWithAsset: React.FC<{
  assetId: string;
  currentImage: 'before' | 'after';
  imageType: 'markup' | 'cropped' | 'original';
  description?: string | null;
  onClose: () => void;
  onNavigate: (image: 'before' | 'after') => void;
}> = ({ assetId, currentImage, imageType, description, onClose, onNavigate }) => {
  const { asset, loading } = useAssetById(assetId);

  if (loading || !asset) {
    return null;
  }

  // Helper to get URLs based on imageType
  const getImageUrl = (type: 'primary' | 'secondary') => {
    const isSecondary = type === 'secondary';
    switch (imageType) {
      case 'markup':
        return isSecondary
          ? (asset.secondary_markup_url || asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_markup_url || asset.primary_cropped_url || asset.primary_original_url || asset.primary_image);
      case 'cropped':
        return isSecondary
          ? (asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_cropped_url || asset.primary_original_url || asset.primary_image);
      case 'original':
        return isSecondary
          ? (asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_original_url || asset.primary_image);
      default:
        return isSecondary
          ? (asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_cropped_url || asset.primary_original_url || asset.primary_image);
    }
  };

  // Determine chronological order
  const primaryDate = asset.version ? parseInt(asset.version) : 0;
  const secondaryDate = asset.secondary_version ? parseInt(asset.secondary_version) : 0;
  const shouldSwapForChronology = primaryDate > secondaryDate && getImageUrl('secondary');

  const beforeImageUrl = shouldSwapForChronology ? getImageUrl('secondary') : getImageUrl('primary');
  const afterImageUrl = shouldSwapForChronology ? getImageUrl('primary') : getImageUrl('secondary');
  const beforeVersion = shouldSwapForChronology ? asset.secondary_version : asset.version;
  const afterVersion = shouldSwapForChronology ? asset.version : asset.secondary_version;

  return (
    <EventImageLightbox
      isOpen={true}
      beforeImageUrl={beforeImageUrl}
      afterImageUrl={afterImageUrl}
      currentImage={currentImage}
      beforeVersion={beforeVersion}
      afterVersion={afterVersion}
      assetTitle={asset.title || asset.slug}
      imageType={imageType}
      description={description}
      onClose={onClose}
      onNavigate={onNavigate}
    />
  );
};

export default DiffDetail;